import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { toast } from '../../components/toasts';
import { Button, ErrorText, Modal } from '../../components/ui';
import { post } from '../../lib/api';
import { useOperators, usePresence } from '../../lib/queries';
import { AttendanceDetails } from '../../lib/types';

/**
 * O responsável chama outro operador para ajudar: ele entra no atendimento na hora (e é avisado),
 * conta o próprio tempo e começa escrevendo em anotação interna. O atendimento continua com você.
 */
export function InviteHelpModal({ attendance, onClose }: { attendance: AttendanceDetails; onClose: () => void }) {
  const qc = useQueryClient();
  const helping = new Set(attendance.participants?.map((p) => p.userId) ?? []);
  const operators = (useOperators().data ?? []).filter((o) => o.id !== attendance.operatorId && !helping.has(o.id));
  const online = usePresence().data;
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    setError(null);
    try {
      await post(`/attendances/${attendance.id}/invite`, { userId: target });
      void qc.invalidateQueries({ queryKey: ['attendance', attendance.id] });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      toast('Pedido de ajuda enviado', 'ok');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Pedir ajuda"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!target} loading={saving} onClick={() => void submit()}>
            Chamar para ajudar
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-fg-soft">
        Quem você chamar entra neste atendimento e é avisado. O atendimento continua com você (só você finaliza ou transfere); o tempo de cada um é
        registrado à parte no Agidesk.
      </p>
      <div className="max-h-64 overflow-y-auto rounded-md border border-border">
        {operators.length === 0 && <div className="px-3 py-2 text-sm text-fg-soft">Nenhum outro operador disponível.</div>}
        {operators.map((o) => (
          <button
            key={o.id}
            onClick={() => setTarget(o.id)}
            className={clsx('flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-0', target === o.id ? 'bg-accent/10' : 'hover:bg-muted')}
          >
            <span className={clsx('size-2 rounded-full', online?.has(o.id) ? 'bg-ok' : 'bg-border')} title={online?.has(o.id) ? 'online' : 'offline'} />
            {o.name}
          </button>
        ))}
      </div>
      <ErrorText error={error} />
    </Modal>
  );
}
